import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Red de seguridad para fallos de RENDER.
 *
 * Los errores de red ya los cubre QueryBoundary, pero si el fallo ocurre
 * mientras React pinta (un dato con la forma equivocada, por ejemplo), React
 * desmonta el arbol ENTERO y el usuario se queda mirando una pagina en blanco
 * sin ninguna pista. Le paso a Alex el 12 de agosto con la Cuenta de
 * resultados: la pantalla no decia nada y parecia que la app estaba muerta.
 *
 * Con esto, un fallo de una pantalla se queda en esa pantalla y ademas se
 * cuenta lo que pasa y como salir.
 */

interface Props {
  children: ReactNode;
  /** Cambia cuando cambia la ruta: resetea el error al navegar. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // A la consola con toda la pinta: es donde miraremos al depurar.
    console.error('[alexia] fallo de render', error, info.componentStack);
  }

  componentDidUpdate(prev: Props): void {
    // Al navegar a otra pantalla se limpia: el error era de la anterior.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="stack">
        <div className="empty">
          <p className="empty__title">Esta pantalla no se pudo mostrar</p>
          <p className="empty__text">
            Ha sido un fallo nuestro, no algo que hayas hecho mal. El resto de la aplicación
            sigue funcionando: puedes seguir por el menú de la izquierda.
          </p>
          <div className="row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
            <button type="button" className="btn" onClick={() => window.location.reload()}>
              Volver a cargar
            </button>
          </div>
          <details style={{ marginTop: '1.25rem', textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
              Detalle técnico (por si nos lo quieres mandar)
            </summary>
            <pre
              style={{
                marginTop: '0.5rem',
                fontSize: '0.8rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
